import React from 'react';
import { AnyProps } from '@tanium/coreui-utils';
import { Box } from '@tanium/react-box';
import { css, cx } from '@tanium/react-emotion-9';
import { DateIcon } from '@tanium/react-graphics';
import { TextInput } from '@tanium/react-text-input';
import { LayoutStyleProps } from '@tanium/style-system';
import { DatePickerSize } from './DatePickerSize';
import { Icon } from './themeDefinitions';

const noOutlineStyle = { ':focus': { outline: 'none' } };

const testInputClassName = css(noOutlineStyle, {
  // It's pretty trick to get a theme in here without hooks and without creating too many extra
  // layers. So this is hardcoded until COREUI-1452. It's off the 8px slightly due to border widths.
  paddingRight: 30,
  width: '100%',
});

// Disable pointer events for the calendar icon, so clicks on the icon go through to the text input.
const iconNoPointer = css({ pointerEvents: 'none' });

interface Props extends AnyProps, Pick<LayoutStyleProps, 'width'> {
  /**
   * A class to apply to the TextInput.
   */
  className?: string;
  /**
   * Allows a 'sm' or 'lg' size to be specified.
   */
  size?: DatePickerSize;
}

/**
 * Input component for the DatePicker. The ref forwarding is significant; ReactDatePicker does some
 * inspection of the DatePickerInput and only sets focus if the ref it receives is, in fact,
 * focusable.
 */
export const DatePickerInput = React.forwardRef<HTMLInputElement, Props>(
  ({ className, size, width, ...rest }, ref) => (
    <Box position="relative" display="inline-flex" alignItems="center" width={width}>
      <TextInput
        ref={ref}
        className={cx(className, testInputClassName)}
        componentSize={size}
        {...rest}
      />
      <Box
        as={DateIcon}
        position="absolute"
        right={1}
        size={size === 'sm' ? '12px' : 2}
        top={size === 'sm' ? '6px' : 1}
        color={Icon}
        className={iconNoPointer}
      />
    </Box>
  ),
);
